import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const IMAGE_WIDTH = 1672;
const IMAGE_HEIGHT = 941;

// Apple Vision joints measured on the approved hero, all above the app's 0.3 confidence cutoff.
const joints = {
  nose: [1238.3, 257.7], neck: [1285.3, 307.4],
  leftShoulder: [1339.9, 304.7], rightShoulder: [1230.8, 310.1],
  leftElbow: [1415.4, 376.7], rightElbow: [1200.7, 393.8],
  leftWrist: [1488.3, 449.8], rightWrist: [1153.3, 458.3],
  root: [1275.0, 470.3],
  leftHip: [1309.1, 481.2], rightHip: [1241.0, 459.5],
  leftKnee: [1283.7, 625.2], rightKnee: [1170.6, 497.7],
  leftAnkle: [1280.0, 785.8], rightAnkle: [1088.0, 631.8]
} as const;

// Same connections as the iOS app's PoseSkeleton.bones.
const bones = [
  ['nose', 'neck'],
  ['neck', 'leftShoulder'], ['neck', 'rightShoulder'],
  ['leftShoulder', 'leftElbow'], ['leftElbow', 'leftWrist'],
  ['rightShoulder', 'rightElbow'], ['rightElbow', 'rightWrist'],
  ['neck', 'root'],
  ['root', 'leftHip'], ['root', 'rightHip'],
  ['leftHip', 'leftKnee'], ['leftKnee', 'leftAnkle'],
  ['rightHip', 'rightKnee'], ['rightKnee', 'rightAnkle']
] as const;

const jointNames = Object.keys(joints) as (keyof typeof joints)[];
const posePath = bones.map(([a, b]) => `M${joints[a].join(' ')} L${joints[b].join(' ')}`).join(' ');

export const HeroTrackingOverlay: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const measure = () => {
      const { width, height } = svg.getBoundingClientRect();
      setSize(previous => previous.width === width && previous.height === height
        ? previous
        : { width, height });
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  const scale = Math.max(size.width / IMAGE_WIDTH, size.height / IMAGE_HEIGHT);
  const mobile = size.width < 900;
  const x = (size.width - IMAGE_WIDTH * scale) * (mobile ? 1 : 0.5);
  const y = (size.height - IMAGE_HEIGHT * scale) * (mobile ? 0.5 : 0.52);

  return (
    <Box
      component="svg"
      ref={svgRef}
      aria-hidden="true"
      focusable="false"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
        overflow: 'hidden',
        visibility: size.width > 0 && size.height > 0 ? 'visible' : 'hidden',
        '& .hero-pose': { opacity: 1, animation: 'hero-pose-flicker 7.8s ease-in-out infinite' },
        '& .hero-ball': { opacity: 1, animation: 'hero-ball-flicker 6.4s ease-in-out infinite' },
        '@keyframes hero-pose-flicker': {
          '0%, 32%, 100%': { opacity: 1 },
          '44%': { opacity: 0.94 },
          '51%': { opacity: 0.82 },
          '59%, 72%': { opacity: 0.98 },
          '83%': { opacity: 0.9 }
        },
        '@keyframes hero-ball-flicker': {
          '0%, 26%, 100%': { opacity: 1 },
          '39%': { opacity: 0.86 },
          '49%, 68%': { opacity: 1 },
          '81%': { opacity: 0.9 }
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& .hero-pose, & .hero-ball': { animation: 'none' }
        }
      }}
    >
      <g transform={`translate(${x} ${y}) scale(${scale})`}>
        <g className="hero-pose" fill="none" stroke="rgba(255,255,255,0.75)" strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.35))' }}>
          <path
            d={posePath}
            strokeWidth="3"
            vectorEffect="non-scaling-stroke"
          />
          {jointNames.map(name => (
            <circle key={name} cx={joints[name][0]} cy={joints[name][1]} r="3.5" fill="rgba(255,255,255,0.9)" strokeWidth="3" vectorEffect="non-scaling-stroke" />
          ))}
        </g>
        <g className="hero-ball" fill="none" stroke="#30D158">
          <rect x="1015" y="390" width="122" height="120" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </g>
      </g>
    </Box>
  );
};
